import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { PropsWithChildren, useCallback } from "react";
import { ModalLoading } from "./modal_loading";

import PrimaryButton from "./primary_button";
import DynamicIcon from "./dynamic_icon";

/**
 * BaseControlledModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  title: string;
  width?: string;
  showConfirmButton?: boolean;
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
  confirmSavingButtonLabel?: string;
  loadingMessage?: string;
  onClose: () => void;
  onSubmit?: () => void;
};

export const BaseControlledModal = ({
  open,
  saving,
  loading,
  title,
  width,
  showConfirmButton,
  cancelButtonLabel,
  confirmButtonLabel,
  confirmSavingButtonLabel,
  loadingMessage,
  children,
  onClose,
  onSubmit,
}: PropsWithChildren<Props>) => {

  const handleSubmit = useCallback(() => {
    if (!saving && !loading && onSubmit !== undefined) onSubmit();
  }, [
    saving,
    loading,
    onSubmit
  ]);

  const handleClose = useCallback(() => {
    if (!saving && !loading) onClose();
  }, [
    saving,
    loading,
    onClose
  ]);

  return (
    <Dialog
      open={open}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: width ?? "60rem",
          },
        },
      }}>

      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        component="div">
        <h2>{title}</h2>
        <div
          onClick={e => handleClose()}>
          <DynamicIcon
            nameIcon="Close"
            size={25}
            className="text-black dark:text-white cursor-pointer"
          />
        </div>
      </DialogTitle>

      <DialogContent>
        <ModalLoading
          loading={saving || loading}
          message={loadingMessage ?? "Processando..."}>
          {children}
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-end flex p-4 gap-4">
          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                {cancelButtonLabel ?? 'Cancelar'}
              </h1>
            </div>
            {
              onSubmit !== undefined &&
              <div className="w-[120px]">
                {saving && (showConfirmButton === undefined || showConfirmButton === true) ? (
                  <PrimaryButton
                    buttonColor="primary-green">
                    {confirmSavingButtonLabel ?? 'Salvando...'}
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    onClick={handleSubmit}
                    buttonColor="primary-green">
                    {confirmButtonLabel ?? 'Salvar'}
                  </PrimaryButton>
                )}
              </div>
            }
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}